import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useCallContext } from '@gem/call/CallView/CallView.context';

import { CallConnectionQuality } from '../../../CallProviders/callProviders.constants';

interface IConnectionWarningPresenter {
  hasPoorConnection: boolean;
  hasReconnecting: boolean;
  connectionQualityNumber: number;
  connectionQuality: CallConnectionQuality;
  connectionQualityNormalisedNumber: number;
  setQualityNumber(value: number): void;
  setHasPoorConnection(value: boolean): void;
}

const connectionQualityMapToNumber: { [key: string]: number } = {
  [CallConnectionQuality.Poor]: 1,
  [CallConnectionQuality.Good]: 2,
  [CallConnectionQuality.Excellent]: 3,
};

const SHOW_BAD_CONNECTION_VALUE = 0.5;
const HIDE_BAD_CONNECTION_VALUE = 0.675;

export const useConnectionWarningPresenter = (): IConnectionWarningPresenter => {
  const context = useCallContext();

  const presenter = useLocalObservable<IConnectionWarningPresenter>(() => ({
    connectionQualityNumber: connectionQualityMapToNumber[CallConnectionQuality.Excellent],
    hasPoorConnection: false,

    get connectionQualityNormalisedNumber(): number {
      return presenter.connectionQualityNumber / 3;
    },

    setHasPoorConnection(value: boolean): void {
      presenter.hasPoorConnection = value;
    },

    get connectionQuality(): CallConnectionQuality {
      return context.call.connectionQuality;
    },

    get hasReconnecting(): boolean {
      return presenter.connectionQuality === CallConnectionQuality.Lost;
    },

    setQualityNumber(value: number): void {
      presenter.connectionQualityNumber = value;
    },
  }));

  useEffect(() => {
    const interval = setInterval(() => {
      const qualityNumber = connectionQualityMapToNumber[presenter.connectionQuality];
      if (qualityNumber === undefined) return;

      presenter.setQualityNumber((qualityNumber + presenter.connectionQualityNumber) / 2);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (
      presenter.connectionQualityNormalisedNumber < SHOW_BAD_CONNECTION_VALUE &&
      presenter.hasPoorConnection
    ) {
      return;
    }
    if (
      presenter.connectionQualityNormalisedNumber < SHOW_BAD_CONNECTION_VALUE &&
      !presenter.hasPoorConnection
    ) {
      presenter.setHasPoorConnection(true);
      return;
    }

    if (
      presenter.connectionQualityNormalisedNumber > HIDE_BAD_CONNECTION_VALUE &&
      presenter.hasPoorConnection
    ) {
      presenter.setHasPoorConnection(false);
      return;
    }
  }, [presenter.connectionQualityNormalisedNumber]);

  return presenter;
};
